import type { AtsJobVacancy } from '@/generated/graphql/types';
import { DOCUMENTS } from '@/shared/components/input-file/utils';
import { useCountryCodesWithDefault } from '@/shared/hooks/use-default-country-code';
import Upload from '@crehana/compass.icons.outline/Upload';
import { AtsCandidateFormVisibilityTypeEnum } from '@crehana/compass.knowledgehub/types';
import { Input } from '@crehana/compass.ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@crehana/compass.ui/select';
import { cn } from '@crehana/compass.ui/utils';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@crehana/forms/components';
import { useFormContext } from '@crehana/forms/rhf';
import {
  ensureHttps,
  parsePhoneValue,
  processLinkedInOnBlur,
  processLinkedInOnChange,
  updatePhoneValue,
} from '../../utils';
import { useTranslationCandidateForm } from '../hooks/use-translations';
import type { TCandidateForm } from '../types';
import { UploadCandidateFormAttachments } from './upload-candidate-form-attachments';

type TGeneralInformationProps = {
  jobVacancyPublicInfo: AtsJobVacancy;
  organizationId: number;
  preview?: boolean;
};

export const GeneralInformation = ({
  jobVacancyPublicInfo,
  organizationId,
  preview,
}: TGeneralInformationProps) => {
  const { t } = useTranslationCandidateForm();
  const { control } = useFormContext<TCandidateForm>();
  const { countryCodes, defaultCountryCode } = useCountryCodesWithDefault();
  //Get the personal info map
  const personalInfoMap = Object.fromEntries(
    jobVacancyPublicInfo?.personal_info?.map(item => [
      item.label,
      item.visibility,
    ]) || [],
  );

  return (
    <div>
      {/* Name */}
      <div className="tw-flex tw-w-full tw-mt-3.2">
        <FormField
          control={control}
          name="name"
          render={({ field, fieldState: { error } }) => (
            <FormItem className="tw-w-full">
              <FormLabel className="tw-font-subtitle4">
                {t('NAMES')}
                <span className="tw-text-alert-500">{t('REQUIRED_ICON')}</span>
              </FormLabel>
              <FormControl className="tw-w-full">
                <Input
                  className={cn(
                    'tw-flex-1  tw-bg-neutral-light-100',
                    error && 'tw-border-alert-400',
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {/* Last name */}
      <div className="tw-flex tw-w-full tw-mt-3.2">
        <FormField
          control={control}
          name="last_name"
          render={({ field, fieldState: { error } }) => (
            <FormItem className="tw-w-full">
              <FormLabel className="tw-font-subtitle4">
                {t('LAST_NAME')}
                <span className="tw-text-alert-500">{t('REQUIRED_ICON')}</span>
              </FormLabel>
              <FormControl className="tw-w-full">
                <Input
                  className={cn(
                    'tw-flex-1  tw-bg-neutral-light-100',
                    error && 'tw-border-alert-400',
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {/* Email */}
      <div className="tw-flex tw-w-full tw-mt-3.2">
        <FormField
          control={control}
          name="email"
          render={({ field, fieldState: { error } }) => (
            <FormItem className="tw-w-full">
              <FormLabel className="tw-font-subtitle4">
                {t('EMAIL')}
                <span className="tw-text-alert-500">{t('REQUIRED_ICON')}</span>
              </FormLabel>
              <FormControl className="tw-w-full">
                <Input
                  className={cn(
                    'tw-flex-1  tw-bg-neutral-light-100',
                    error && 'tw-border-alert-400',
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {/* Phone */}
      {personalInfoMap['PERSONAL_INFO_PHONE'] !==
        AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility && (
        <div className="tw-flex tw-w-full tw-mt-3.2">
          <FormField
            control={control}
            name="number_phone"
            render={({ field, fieldState: { error } }) => {
              const { countryCode, phoneNumber } = parsePhoneValue(
                field.value,
                countryCodes,
              );
              const selectedCountryCode = countryCode || defaultCountryCode;

              return (
                <FormItem className="tw-w-full">
                  <FormLabel className="tw-font-subtitle4">
                    {t('NUMBER_PHONE')}
                    {personalInfoMap['PERSONAL_INFO_PHONE'] ===
                      AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility && (
                      <span className="tw-text-alert-500">
                        {t('REQUIRED_ICON')}
                      </span>
                    )}
                    <p className="tw-font-body5 tw-font-normal tw-text-neutral-light-700 tw-my-0.8">
                      {t('DESCRIPTION_PHONE')}
                    </p>
                  </FormLabel>

                  <FormControl className="tw-w-full">
                    <div
                      className={cn(
                        'tw-flex   tw-w-full tw-items-center tw-bg-neutral-light-100 tw-border tw-border-solid tw-border-neutral-light-500 tw-rounded-0.8 tw-overflow-hidden',
                        error && 'tw-border-alert-400',
                      )}
                    >
                      <Select
                        value={countryCode || defaultCountryCode}
                        onValueChange={code => {
                          field.onChange(updatePhoneValue(code, phoneNumber));
                        }}
                      >
                        <SelectTrigger className="tw-w-[12rem] tw-py-0.8 tw-flex-shrink-0 tw-h-full tw-border-0 tw-rounded-0 tw-bg-transparent tw-shadow-none">
                          <SelectValue>{selectedCountryCode}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {countryCodes.map(country => (
                            <SelectItem
                              key={country.iso ?? country.value}
                              value={country.value}
                            >
                              {country.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="tw-flex-1 tw-min-w-0 tw-border-l tw-border-solid tw-border-neutral-light-500">
                        <Input
                          className={cn(
                            'tw-w-full tw-h-full tw-py-0.8 tw-border-0 tw-rounded-0 tw-bg-transparent tw-shadow-none focus-visible:tw-ring-0',
                          )}
                          placeholder={t('DESCRIPTION_PHONE')}
                          value={phoneNumber}
                          onChange={e => {
                            const newValue = e.target.value;
                            // Solo permitir números
                            const regex = /^[0-9]*$/;
                            if (!regex.test(newValue)) {
                              return;
                            }
                            field.onChange(
                              updatePhoneValue(selectedCountryCode, newValue),
                            );
                          }}
                        />
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
      )}
      {/* Country */}
      {personalInfoMap['PERSONAL_INFO_COUNTRY'] !==
        AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility && (
        <div className="tw-flex tw-w-full tw-mt-3.2">
          <FormField
            control={control}
            name="country"
            render={({ field, fieldState: { error } }) => (
              <FormItem className="tw-w-full">
                <FormLabel className="tw-font-subtitle4">
                  {t('COUNTRY')}
                  {personalInfoMap['PERSONAL_INFO_COUNTRY'] ===
                    AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility && (
                    <span className="tw-text-alert-500">
                      {t('REQUIRED_ICON')}
                    </span>
                  )}
                </FormLabel>
                <FormControl className="tw-w-full">
                  <Input
                    className={cn(
                      'tw-flex-1  tw-bg-neutral-light-100',
                      error && 'tw-border-alert-400',
                    )}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
      {/* Resume */}
      {personalInfoMap['PERSONAL_INFO_RESUME'] !==
        AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility && (
        <div className="tw-flex tw-w-full tw-mt-3.2">
          <FormField
            control={control}
            name="resume"
            render={({ field }) => (
              <FormItem className="tw-w-full">
                <FormLabel className="tw-font-subtitle4">
                  {t('CURRICULUM')}
                  {(personalInfoMap['PERSONAL_INFO_RESUME'] ===
                    AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility ||
                    personalInfoMap['PERSONAL_INFO_RESUME'] ===
                      AtsCandidateFormVisibilityTypeEnum.AlwaysRequiredCandidateFormVisibility) && (
                    <span className="tw-text-alert-500">
                      {t('REQUIRED_ICON')}
                    </span>
                  )}
                  <p className="tw-font-body5 tw-font-normal tw-text-neutral-light-700 tw-my-0.8">
                    {t('CURRICULUM_DESCRIPTION')}
                  </p>
                </FormLabel>
                <FormControl>
                  <div className="tw-flex tw-w-full">
                    {preview ? (
                      <div className="tw-btn-primary-xs tw-btn-icon tw-btn-disabled">
                        <Upload
                          size={16}
                          className="tw-text-current group-hover:tw-text-primary-light-500"
                        />
                        {t('UPLOAD_ATTACHMENT')}
                      </div>
                    ) : (
                      <UploadCandidateFormAttachments
                        onUpdateAttachment={value => {
                          field.onChange(value?.toString());
                        }}
                        originalId={organizationId || 0}
                        acceptFileTypes={DOCUMENTS['application/pdf']}
                      />
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
      {/* Linkedin */}
      {personalInfoMap['PERSONAL_INFO_LINKEDIN'] !==
        AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility && (
        <div className="tw-flex tw-w-full tw-mt-3.2">
          <FormField
            control={control}
            name="linkedin_url"
            render={({ field, fieldState: { error } }) => {
              const savedValue = field.value || '';
              const displayValue = savedValue ? ensureHttps(savedValue) : '';

              return (
                <FormItem className="tw-w-full">
                  <FormLabel className="tw-font-subtitle4">
                    {t('LINKEDIN')}
                    {personalInfoMap['PERSONAL_INFO_LINKEDIN'] ===
                      AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility && (
                      <span className="tw-text-alert-500">
                        {t('REQUIRED_ICON')}
                      </span>
                    )}
                  </FormLabel>
                  <FormControl className="tw-w-full">
                    <Input
                      placeholder="https://"
                      className={cn(
                        'tw-flex-1 tw-bg-neutral-light-100',
                        error && 'tw-border-alert-400',
                      )}
                      value={displayValue}
                      onChange={e => {
                        field.onChange(processLinkedInOnChange(e.target.value));
                      }}
                      onBlur={e => {
                        field.onChange(processLinkedInOnBlur(e.target.value));
                        field.onBlur();
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
      )}
    </div>
  );
};
